// Centralized date/number formatting for the web app.
// All renderers default to Asia/Riyadh and to the user's active language.
// To override, pass an explicit `locale` ('en' | 'ar') or `timeZone`.

import i18n from './i18n';

type Inp = string | number | Date | null | undefined;

const TZ = 'Asia/Riyadh';

function activeLocale(override?: 'en' | 'ar'): string {
  if (override) return override === 'ar' ? 'ar-SA' : 'en-GB';
  const lang = (i18n.language || 'en').slice(0, 2);
  return lang === 'ar' ? 'ar-SA' : 'en-GB';
}

function toDate(v: Inp): Date | null {
  if (v == null || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isFinite(d.getTime()) ? d : null;
}

export function formatDate(v: Inp, opts?: { locale?: 'en' | 'ar'; timeZone?: string }): string {
  const d = toDate(v);
  if (!d) return '—';
  return new Intl.DateTimeFormat(activeLocale(opts?.locale), {
    timeZone: opts?.timeZone ?? TZ,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(d);
}

// PR-MAY19-TRIP-DATE-CLEAN — single helper used by trips list, calendar,
// detail drawer, ledgers, exports. trip_date is a `date` column (no time
// component); the FE used to render the raw ISO string ("2026-05-17T00:
// 00:00.000Z") because Date(...) of a YMD string injects midnight UTC.
// formatTripDate forces Asia/Riyadh and drops the time → "17 May 2026".
export function formatTripDate(v: Inp): string {
  return formatDate(v);
}

export function formatDateTime(v: Inp, opts?: { locale?: 'en' | 'ar'; timeZone?: string }): string {
  const d = toDate(v);
  if (!d) return '—';
  return new Intl.DateTimeFormat(activeLocale(opts?.locale), {
    timeZone: opts?.timeZone ?? TZ,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

export function formatTime(v: Inp, opts?: { locale?: 'en' | 'ar'; timeZone?: string }): string {
  const d = toDate(v);
  if (!d) return '—';
  return new Intl.DateTimeFormat(activeLocale(opts?.locale), {
    timeZone: opts?.timeZone ?? TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

// "5m ago", "2h ago", "3d ago" — falls back to formatDate for >30d.
export function formatRelative(v: Inp, opts?: { locale?: 'en' | 'ar' }): string {
  const d = toDate(v);
  if (!d) return '—';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  const isAr = activeLocale(opts?.locale).startsWith('ar');
  if (sec < 60) return isAr ? 'الآن' : 'just now';
  if (sec < 3600) return (isAr ? 'منذ ' : '') + Math.floor(sec / 60) + (isAr ? ' د' : 'm ago');
  if (sec < 86400) return (isAr ? 'منذ ' : '') + Math.floor(sec / 3600) + (isAr ? ' س' : 'h ago');
  if (sec < 86400 * 30) return (isAr ? 'منذ ' : '') + Math.floor(sec / 86400) + (isAr ? ' ي' : 'd ago');
  return formatDate(d, opts);
}
